import React, { useEffect } from 'react';
import {
  CloseNotificationIcon,
  NotificationIcon,
} from './NotificationIcons';
import type {
  ErrorNotificationState,
  NotificationId,
  NotificationState,
  SuccessNotificationState,
  UploadNotificationState,
  WarningNotificationState,
} from './notification-types';
import {
  notificationAutoCloseMs,
  normalizeProgress,
} from './notification-utils';

export const NotificationViewport: React.FC<{
  notifications: NotificationState[];
  onClose: (id: NotificationId) => void;
}> = ({ notifications, onClose }) => {
  if (notifications.length === 0) return null;

  return (
    <div className='notification-viewport' aria-live='polite'>
      {notifications.map((notification) =>
        renderNotification(notification, onClose),
      )}
    </div>
  );
};

function renderNotification(
  notification: NotificationState,
  onClose: (id: NotificationId) => void,
): React.ReactNode {
  switch (notification.type) {
    case 'upload':
      return (
        <UploadNotification key={notification.id} notification={notification} />
      );
    case 'success':
      return (
        <SuccessNotification
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      );
    case 'error':
      return (
        <ErrorNotification
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      );
    case 'warning':
      return (
        <WarningNotification
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      );
  }

  return null;
}

const UploadNotification: React.FC<{
  notification: UploadNotificationState;
}> = ({ notification }) => {
  return (
    <div
      className={`upload-notification${notification.isClosing ? ' notification--closing' : ''}`}
      role='status'
    >
      <UploadNotificationLoader progress={notification.progress} />
      <div className='upload-notification__content'>
        <div className='upload-notification__title'>{notification.title}</div>
        <div className='upload-notification__progress'>
          {Math.round(notification.progress)}%
        </div>
      </div>
    </div>
  );
};

const SuccessNotification: React.FC<{
  notification: SuccessNotificationState;
  onClose: (id: NotificationId) => void;
}> = ({ notification, onClose }) => (
  <NotificationCard notification={notification} onClose={onClose} />
);

const ErrorNotification: React.FC<{
  notification: ErrorNotificationState;
  onClose: (id: NotificationId) => void;
}> = ({ notification, onClose }) => (
  <NotificationCard notification={notification} onClose={onClose} />
);

const WarningNotification: React.FC<{
  notification: WarningNotificationState;
  onClose: (id: NotificationId) => void;
}> = ({ notification, onClose }) => (
  <NotificationCard notification={notification} onClose={onClose} />
);

const NotificationCard: React.FC<{
  notification:
    | SuccessNotificationState
    | ErrorNotificationState
    | WarningNotificationState;
  onClose: (id: NotificationId) => void;
}> = ({ notification, onClose }) => {
  const isAutoClosing = notification.autoClose !== false;
  const autoCloseMs = notificationAutoCloseMs[notification.type];

  useEffect(() => {
    if (!isAutoClosing) return;

    const timeoutId = window.setTimeout(() => {
      onClose(notification.id);
    }, autoCloseMs);

    return () => window.clearTimeout(timeoutId);
  }, [autoCloseMs, isAutoClosing, notification.id, onClose]);

  return (
    <div
      className={`notification-card notification-card--${notification.type}${notification.isClosing ? ' notification--closing' : ''}`}
      role='status'
    >
      <NotificationIcon type={notification.type} />
      <div className='notification-card__content'>
        <div className='notification-card__title'>{notification.title}</div>
        {notification.description ? (
          <div className='notification-card__description'>
            {notification.description}
          </div>
        ) : null}
      </div>
      <button
        className='notification-card__close'
        type='button'
        aria-label='Close notification'
        onClick={() => onClose(notification.id)}
      >
        <CloseNotificationIcon />
      </button>
    </div>
  );
};

const UploadNotificationLoader: React.FC<{ progress: number }> = ({
  progress,
}) => {
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - normalizeProgress(progress) / 100);

  return (
    <svg
      className='upload-notification__loader'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      aria-hidden='true'
    >
      <circle
        cx='10'
        cy='10'
        r={radius}
        stroke='#BAE7FF'
        strokeWidth='2'
      />
      <circle
        className='upload-notification__loader-progress'
        cx='10'
        cy='10'
        r={radius}
        stroke='#1890FF'
        strokeWidth='2'
        strokeLinecap='round'
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
      />
    </svg>
  );
};
